/** @deprecated */

import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';

import {
	resumeSubscription,
	SubscriptionActionTypes,
	useBusinessLayer,
	useDTicketSubscriptionOffering,
	useSubscription,
	useSubscriptionActions,
	useSubscriptionTicketActions,
	useUser,
} from '@mopla/business-logic';
import {
	AdaptiveActionsBlock,
	CheckmarkIcon,
	Loader,
	MplButton,
} from '@mopla/ui';
import { getDTicketType, isAfterTenthOfMonth } from '@mopla/utils';

import { pushSubscriptionManagementEvent } from '../../../app/userTracking/events';

import { ConfirmationStep } from './steps/Confirmation';
import { FirstStep } from './steps/First';
import { SummaryStep } from './steps/Summary';
import {
	CONFIRMATION_STEP,
	defaultValues,
	MONTH_SELECTION_STEP,
	SUMMARY_STEP,
} from './constants';
import {
	CustomStepper,
	DrawerContentWrapper,
	LoaderWrapper,
	ScrollableWrapper,
} from './DTicketSubscription.styles';
import { DTicketSubscriptionWrapper } from './DTicketSubscriptionWrapper';
import { getSavedAlternateInvoiceAddress } from './helpers';
import { PriceInfoBlock } from './PriceInfoBlock';
import { SubscriptionFormValidationSchema } from './SubscriptionValidation';
import { ISubscriptionFormValues } from './types';
import { ValidityModal } from './ValidityModal';

interface IProps {
	open: boolean;
	onClose: VoidFunction;
}

/** TODO this component is pretty much the same as DTicketSubscription. Consider further refactoring and reuse code */
export const ResumeSubscription: FC<IProps> = (props) => {
	const { open, onClose } = props;
	const { t } = useTranslation(['dticket']);
	const [activeStep, setActiveStep] = useState<string>(MONTH_SELECTION_STEP);
	const { subscriptionData: subscription } = useSubscription();
	const { fetchSubscriptions } = useSubscriptionActions();
	const { fetchTickets: refetchTickets } = useSubscriptionTicketActions();
	const [isValidityModalVisible, setIsValidityModalVisible] = useState(false);
	const bl = useBusinessLayer();

	const { userData } = useUser();

	const methods = useForm<ISubscriptionFormValues>({
		defaultValues: {
			...defaultValues,
			firstName: userData?.firstName,
			surname: userData?.lastName,
			phoneNumber: userData?.phone,
			street: userData?.address?.street,
			streetNumber: userData?.address?.streetNumber,
			postCode: userData?.address?.zipCode,
		},
		resolver: yupResolver(SubscriptionFormValidationSchema),
		mode: 'onBlur',
	});
	const { formState, reset, getValues, trigger, watch } = methods;
	const { isDirty, errors } = formState;
	const monthOfStartValue = watch('monthOfStart');

	const { subscriptionOffering, subscriptionOfferByMonth } =
		useDTicketSubscriptionOffering(monthOfStartValue);
	const dTicketType = getDTicketType(subscriptionOffering?.subscriptionType);
	const isPriceZero = subscriptionOffering?.price === 0;

	const navigate = useNavigate();
	const location = useLocation();
	const query = useMemo(
		() => new URLSearchParams(location.search),
		[location.search]
	);

	const [isAcceptTariffs, setIsAcceptTariffs] = useState<boolean>(false);
	const [isAcceptEarlyServiceStart, setIsAcceptEarlyServiceStart] =
		useState<boolean>(false);
	const [isUpdating, setIsUpdating] = useState<boolean>(false);

	const isMonthSelectionStep = activeStep === MONTH_SELECTION_STEP;
	const isSummaryStep = activeStep === SUMMARY_STEP;
	const isConfirmationStep = activeStep === CONFIRMATION_STEP;

	const steps = [MONTH_SELECTION_STEP, SUMMARY_STEP];
	const stepsAmount = steps.length;
	const activeStepIndex = steps.findIndex((step) => step === activeStep);

	useEffect(() => {
		if (!userData || !userData.address) return;

		const { lastName, dateOfBirth, address, firstName, phone } = userData;
		const personalData: ISubscriptionFormValues = {
			monthOfStart: getValues('monthOfStart') || '',
			firstName: firstName || '',
			surname: lastName || '',
			phoneNumber: phone || '',
			day: dayjs(dateOfBirth).date().toString(),
			month: dayjs(dateOfBirth).month().toString(),
			year: dayjs(dateOfBirth).year().toString(),
			street: address.street || '',
			streetNumber: address.streetNumber || '',
			postCode: address.zipCode || '',
			location: address.city || '',
			companyName: subscription?.companyName || '',
		};
		reset(personalData);
	}, [userData, reset, getValues]);

	const nextStep = () => {
		const nextIndex = activeStepIndex + 1;
		setActiveStep(
			nextIndex > stepsAmount ? steps[stepsAmount - 1] : steps[nextIndex]
		);
	};

	const nextStepHandler = async () => {
		if (isMonthSelectionStep) {
			const isValid = await trigger('monthOfStart');
			const monthOfStart = getValues('monthOfStart');

			setIsValidityModalVisible(isAfterTenthOfMonth(monthOfStart));

			if (!isValid) return;
			return nextStep();
		}

		setIsUpdating(true);

		await new Promise<void>((resolve) => {
			bl.dispatch(
				resumeSubscription({
					subscriptionId: subscription?.id as string,
				})
			);

			bl.watchActions({
				types: SubscriptionActionTypes.ResumeSubscriptionResult,
				callback: (action) => {
					if (action.payload?.error) {
						console.error(
							'Error on creating subscription: ',
							action.payload.error
						);
					} else {
						setIsUpdating(false);
						setActiveStep(CONFIRMATION_STEP);

						pushSubscriptionManagementEvent('resumeSubscription', {});
					}

					resolve();
				},
			});
		});
	};

	const previousStepHandler = () => {
		const prevIndex = activeStepIndex - 1;
		if (prevIndex < 0) return handleClose();
		return setActiveStep(steps[prevIndex]);
	};
	const redirectToHomePage = () =>
		!Capacitor.isNativePlatform()
			? navigate({ pathname: '/welcome', search: 'tryOurApp' })
			: navigate('/home');

	const handleClose = () => {
		fetchSubscriptions();
		refetchTickets();
		// Condition if subscription flow started by passwordless
		const startedByPasswordless =
			query.has('subscription') && query.has('oobCode');
		startedByPasswordless ? redirectToHomePage() : onClose();

		setIsAcceptTariffs(false);
		setActiveStep(MONTH_SELECTION_STEP);
		reset(defaultValues);
	};

	const getIsDisabled = () => {
		if (isMonthSelectionStep) return !isDirty || !!errors.monthOfStart;

		const hasErrors = !isDirty || Object.keys(errors).length > 0;

		if (isSummaryStep)
			return !isAcceptTariffs || !isAcceptEarlyServiceStart || isUpdating;

		return !isSummaryStep && hasErrors;
	};

	const getContent = (): ReactNode => {
		if (isMonthSelectionStep)
			return (
				<FirstStep
					type="resume"
					subscription={subscription}
					subscriptionOffering={subscriptionOffering}
					selectedMonth={subscriptionOfferByMonth}
				/>
			);

		if (isUpdating) {
			return (
				<LoaderWrapper>
					<Loader />
				</LoaderWrapper>
			);
		}

		return (
			<SummaryStep
				invoiceData={getSavedAlternateInvoiceAddress(
					userData?.alternateInvoiceAddress || null
				)}
				showInvoiceRecipientBlock={!!userData?.alternateInvoiceAddress}
				isAcceptTariffs={isAcceptTariffs}
				setIsAcceptTariffs={setIsAcceptTariffs}
				isAcceptEarlyServiceStart={isAcceptEarlyServiceStart}
				setIsAcceptEarlyServiceStart={setIsAcceptEarlyServiceStart}
				isPriceZero={isPriceZero}
				dTicketType={dTicketType}
			/>
		);
	};

	const getHeader = (): string => {
		if (isMonthSelectionStep)
			return t('header.resume_subscription', { dTicketType });
		if (isConfirmationStep) return t('header.thanks_for_subscription');
		return t('header.summary');
	};

	const getButtonText = (): string => {
		if (isMonthSelectionStep) return t('button.next');

		return t('button.subscribe');
	};

	const handleSubmitValidityModal = () => {
		setIsValidityModalVisible(false);
	};

	const handleCancelValidityModal = () => {
		setIsValidityModalVisible(false);
		previousStepHandler();
	};

	return (
		<>
			<ValidityModal
				isOpen={isValidityModalVisible}
				onSubmit={handleSubmitValidityModal}
				onCancel={handleCancelValidityModal}
			/>
			<FormProvider {...methods}>
				<DTicketSubscriptionWrapper
					open={open}
					title={getHeader()}
					onClose={handleClose}
					onBack={activeStepIndex < 0 ? undefined : previousStepHandler}
					isConfirmationStep={isConfirmationStep}
					content={
						<DrawerContentWrapper>
							{isConfirmationStep && (
								<ConfirmationStep
									onSubmit={handleClose}
									distributor={subscription?.companyName || ''}
									dTicketType={dTicketType}
								/>
							)}
							{!isConfirmationStep && (
								<ScrollableWrapper>{getContent()}</ScrollableWrapper>
							)}
						</DrawerContentWrapper>
					}
					footer={
						<>
							{!isConfirmationStep && (
								<>
									<CustomStepper
										variant="dots"
										steps={stepsAmount}
										position="static"
										activeStep={activeStepIndex}
										nextButton={null}
										backButton={null}
									/>
									<AdaptiveActionsBlock
										topBlock={
											isSummaryStep && (
												<PriceInfoBlock
													priceToPay={subscriptionOfferByMonth?.price}
												/>
											)
										}
										mainBlock={
											<MplButton
												color="primaryRevert"
												data-testid="dticket-subscription-submit-button"
												onClick={nextStepHandler}
												disabled={getIsDisabled()}
												fullWidth
											>
												{getButtonText()}
											</MplButton>
										}
										enforceMobile
									/>
								</>
							)}
							{isConfirmationStep && (
								<AdaptiveActionsBlock
									mainBlock={
										<MplButton
											fullWidth
											color="primaryRevert"
											onClick={handleClose}
											endIcon={<CheckmarkIcon />}
										>
											{t('booking:button.conclude')}
										</MplButton>
									}
									enforceMobile
								/>
							)}
						</>
					}
				/>
			</FormProvider>
		</>
	);
};
